import React, { useState, useMemo } from "react";
import { Modal } from "antd";
import { useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";

import TextBase from "../common/text";
import LoginInput from "../common/login-input";
import ButtonGradient from "../common/button-gradient";
import InputError from "../common/input-error/InputError.component";

import { Colors } from "../themes/Colors";
import { SVGImageComponentReact } from "../themes/Images";
import { UserAction } from "../redux/action";
import Utils from "../utils";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { Typography } from "@mui/material";

const ChangePassword = ({ showChangePassword, closeModal }) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});

  /* ---------------- PASSWORD RULES ---------------- */

  const passwordRules = {
    length: form.newPassword.length >= 12 && form.newPassword.length <= 128,
    number: /\d/.test(form.newPassword),
    uppercase: /[A-Z]/.test(form.newPassword),
    lowercase: /[a-z]/.test(form.newPassword),
    special: /[!@#$%^&*]/.test(form.newPassword),
  };

  const passwordsMatch =
    form.newPassword !== "" && form.newPassword === form.confirmPassword;

  /* ---------------- FORM VALID ---------------- */

  const isFormValid = useMemo(() => {
    return (
      form.currentPassword &&
      passwordRules.length &&
      passwordRules.number &&
      form.newPassword &&
      passwordsMatch
    );
  }, [form, passwordRules, passwordsMatch]);

  /* ---------------- INPUT HANDLER ---------------- */

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));

    setErrors((prev) => ({
      ...prev,
      [field]: null,
    }));
  };

  /* ---------------- SAVE ---------------- */

  const handleSave = async () => {
    if (!isFormValid) return;

    const payload = {
      old_password: form.currentPassword,
      new_password1: form.newPassword,
      new_password2: form.confirmPassword,
    };

    try {
      setLoading(true);
      setErrors({});

      await dispatch(UserAction.changePassword(payload));

      Utils.showAlert(1, "Password changed successfully");

      resetForm();
      closeModal();
    } catch (err) {
      setErrors(err || {});
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- RESET ---------------- */

  const resetForm = () => {
    setForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    setErrors({});
    setShowPassword({
      current: false,
      new: false,
      confirm: false,
    });
    setPasswordFocused(false);
  };

  React.useEffect(() => {
    if (!showChangePassword) {
      resetForm();
    }
  }, [showChangePassword]);

  /* ---------------- ICON ---------------- */

  const getEyeIcon = (visible) =>
    visible
      ? SVGImageComponentReact.IconEyePassword
      : SVGImageComponentReact.IconEyeOffPassword;

  /* ---------------- PASSWORD RULE ITEM ---------------- */

  const Rule = ({ valid, text }) => {
    let color = Colors.grey;
    let Icon = RadioButtonUncheckedIcon;

    if (passwordFocused) {
      if (valid) {
        color = "green";
        Icon = CheckCircleIcon;
      } else {
        color = "red";
        Icon = CancelIcon;
      }
    }

    return (
      <div
        style={{
          color,
          marginTop: "var(--sp-xxs)",
          display: "flex",
          alignItems: "center",
          gap: "var(--sp-xs)",
        }}
      >
        <Icon
          style={{
            fontSize: "var(--fs-md)",
            flexShrink: 0,
            padding: '0',
            margin: '0',
          }}
        />

        <Typography variant="body2" sx={{ fontSize: "var(--fs-md)" }}>{text}</Typography>
      </div>
    );
  };
  const CurrentEye = getEyeIcon(showPassword.current);
  const NewEye = getEyeIcon(showPassword.new);
  const ConfirmEye = getEyeIcon(showPassword.confirm);


  return (
    <Modal
      visible={showChangePassword}
      centered
      footer={null}
      width={"40vw"}
      onCancel={() => {
        resetForm();
        closeModal();
      }}
      closeIcon={
        <CloseIcon
          sx={{ fontSize: "calc(var(--fs-sm)*2)" }}
          onClick={() => {
            resetForm();
            closeModal();
          }}
        />
      }
      bodyStyle={{
        paddingInline: "calc(var(--sp-lg)*1.75)",
        borderRadius: "calc(var(--sp-md2)*1.2)",
      }}
    >
      {/* TITLE */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "calc(var(--sp-lg)*1.5)",
          paddingTop: "calc(var(--sp-md2)*1.25)",
        }}
      >
        <TextBase
          size={"calc(var(--sp-lg)*1.2)"}
          color={Colors.grey2}
          fontWeight={600}
        >
          changePassword
        </TextBase>
      </div>

      {/* CURRENT PASSWORD */}

      <div style={{ marginBottom: "calc(var(--sp-lg)*1.75)" }}>
        <TextBase size={"var(--fs-md2)"} color={Colors.black}>
          currentPassword
        </TextBase>

        <LoginInput
          value={form.currentPassword}
          onChange={(v) => handleChange("currentPassword", v)}
          placeholder="enterPassword"
          type={showPassword.current ? "text" : "password"}
          style={{ top: -20 }}
          rightElement={
            <CurrentEye
              className="cursor-pointer password-eye-icon"
              onClick={() =>
                setShowPassword((p) => ({ ...p, current: !p.current }))
              }
            />
          }
        />

        <InputError
          error={errors.old_password || errors.non_field_errors}
        />
      </div>

      {/* NEW PASSWORD */}

      <div style={{ marginBottom: "calc(var(--sp-lg)*1.75)" }}>
        <TextBase size={"var(--fs-md2)"} color={Colors.black}>
          newPassword
        </TextBase>

        <LoginInput
          value={form.newPassword}
          onChange={(v) => handleChange("newPassword", v)}
          onFocus={() => setPasswordFocused(true)}
          placeholder="enterPassword"
          type={showPassword.new ? "text" : "password"}
          style={{ top: -20 }}
          rightElement={
            <NewEye
              className="cursor-pointer password-eye-icon"
              onClick={() =>
                setShowPassword((p) => ({ ...p, new: !p.new }))
              }
            />
          }
        />

        <InputError error={errors.new_password1} />

        {/* PASSWORD RULES */}

        <div style={{ marginTop: "calc(var(--sp-md2)*1.50943)" }}>
          <Rule valid={passwordRules.length} text="At least 12 characters" />
          <Rule valid={passwordRules.number} text="Contains a number" />
          <Rule valid={passwordRules.uppercase} text="Contains uppercase letter" />
          <Rule valid={passwordRules.lowercase} text="Contains lowercase letter" />
          <Rule valid={passwordRules.special} text="Contains special character" />
        </div>
      </div>

      {/* CONFIRM PASSWORD */}

      <div style={{ marginBottom: "calc(var(--sp-lg)*1.75)" }}>
        <TextBase size={"var(--fs-md2)"} color={Colors.black}>
          confirmPassword
        </TextBase>

        <LoginInput
          value={form.confirmPassword}
          onChange={(v) => handleChange("confirmPassword", v)}
          placeholder="enterPassword"
          type={showPassword.confirm ? "text" : "password"}
          style={{ top: -20 }}
          rightElement={
            <ConfirmEye
              className="cursor-pointer password-eye-icon"
              onClick={() =>
                setShowPassword((p) => ({ ...p, confirm: !p.confirm }))
              }
            />
          }
        />

        {!passwordsMatch && form.confirmPassword && (
          <div style={{ color: "red", fontSize: "var(--fs-sm)" }}>
            Passwords do not match
          </div>
        )}

        <InputError error={errors.new_password2} />
      </div>

      {/* SAVE BUTTON */}

      <div
        style={{
          marginBottom: "calc(var(--sp-md2)*1.9)",
          marginTop: "var(--sp-lg)",
        }}
        className="flex-center-all max-width-100"
      >
        <ButtonGradient
          onClick={handleSave}
          title={loading ? "Saving..." : "save"}
          loading={loading}
          disabled={!isFormValid || loading}
          textStyle={{ fontWeight: 600 }}
        />
      </div>
    </Modal>
  );
};

export default ChangePassword;